import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../../services/api';
import './Admin.css';
import ConfirmModal from '../../components/ConfirmModal';


export default function ManageQuestions() {
    const { quizId } = useParams();
    const navigate = useNavigate();

    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);

    const [formData, setFormData] = useState({
        questionText: '',
        type: 'mcq',
        options: { A: '', B: '', C: '', D: '' },
        correctAnswer: 'A',
        points: 10,
        explanation: ''
    });

    // Confirm Modal & Alert state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger',
        singleButton: false
    });

    const showAlert = (title, message, type = 'info') => {
        setConfirmModal({
            isOpen: true,
            title,
            message,
            type,
            singleButton: true,
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
    };


    useEffect(() => {
        fetchQuizData();
    }, [quizId]);

    const fetchQuizData = async () => {
        try {
            setLoading(true);
            const data = await api.getQuizById(quizId);
            setQuiz(data);
        } catch (err) {
            console.error('Fetch quiz error:', err);
            showAlert('Error', 'Failed to load quiz data', 'danger');
        } finally {
            setLoading(false);
        }
    };


    const handleTypeChange = (type) => {
        if (type === 'tf') {
            setFormData({
                ...formData,
                type: 'tf',
                options: { A: 'True', B: 'False', C: '', D: '' },
                correctAnswer: 'A'
            });
        } else {
            setFormData({
                ...formData,
                type: 'mcq',
                options: { A: '', B: '', C: '', D: '' },
                correctAnswer: 'A'
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingQuestion) {
                await api.updateQuestion(editingQuestion._id, formData);
            } else {
                await api.addQuestion(quizId, formData);
            }
            setShowModal(false);
            setEditingQuestion(null);
            fetchQuizData();
        } catch (err) {
            showAlert('Save Failed', err.message || 'Error saving question', 'danger');
        }
    };


    const openCreateModal = () => {
        setEditingQuestion(null);
        setFormData({
            questionText: '',
            type: 'mcq',
            options: { A: '', B: '', C: '', D: '' },
            correctAnswer: 'A',
            points: 10,
            explanation: ''
        });
        setShowModal(true);
    };

    const openEditModal = (q) => {
        setEditingQuestion(q);
        setFormData({
            questionText: q.questionText,
            type: q.type || 'mcq',
            options: q.options || { A: '', B: '', C: '', D: '' },
            correctAnswer: q.correctAnswer,
            points: q.points || 10,
            explanation: q.explanation || ''
        });
        setShowModal(true);
    };

    const handleDelete = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Question?',
            message: 'Are you sure you want to remove this question from the assessment? This action cannot be undone.',
            type: 'danger',
            singleButton: false,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await api.deleteQuestion(id);
                    fetchQuizData();
                    showAlert('Success', 'Question deleted successfully', 'info');
                } catch (err) {
                    showAlert('Deletion Failed', 'Error deleting question', 'danger');
                }
            }
        });
    };


    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '10rem' }}><div className="spinner"></div></div>;
    if (!quiz) return <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>Quiz not found</div>;

    return (
        <>
            <header className="admin-page-header">
                <button className="btn-action" style={{ marginBottom: '1.5rem', border: 'none', padding: 0, fontWeight: 700, opacity: 0.6 }} onClick={() => navigate('/admin/exams')}>
                    ← Return to Exams
                </button>
                <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                        <h1>{quiz.title}</h1>
                        <p>Architecting <span style={{ color: '#0f172a', fontWeight: 800 }}>{quiz.questions?.length || 0}</span> questions • <span style={{ color: '#0f172a', fontWeight: 800 }}>{quiz.totalPoints}</span> aggregate points</p>
                    </div>
                    <button className="btn-action primary" onClick={openCreateModal}>
                        + Add Question
                    </button>
                </div>
            </header>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {quiz.questions && quiz.questions.length > 0 ? (
                    quiz.questions.map((q, idx) => (
                        <div key={q._id} className="admin-table-section-card" style={{ borderLeft: '4px solid #0f172a', padding: '2.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '2rem' }}>
                                <div>
                                    <p className="label-caps" style={{ color: '#3b82f6', marginBottom: '0.5rem' }}>Item {idx + 1} • {q.type?.toUpperCase()}</p>
                                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>{q.questionText}</h4>
                                </div>
                                <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#94a3b8' }}>{q.points} PTS</span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                                {Object.entries(q.options).map(([key, val]) => (
                                    val && (
                                        <div key={key} style={{
                                            padding: '1.25rem',
                                            borderRadius: '0.75rem',
                                            border: '1px solid',
                                            borderColor: q.correctAnswer === key ? '#10b981' : '#e2e8f0',
                                            background: q.correctAnswer === key ? '#ecfdf5' : '#f8fafc',
                                            color: q.correctAnswer === key ? '#065f46' : '#64748b',
                                            fontWeight: q.correctAnswer === key ? 700 : 500,
                                            fontSize: '0.9375rem',
                                            display: 'flex',
                                            justifyContent: 'space-between'
                                        }}>
                                            <span><span style={{ opacity: 0.5, fontWeight: 800, marginRight: '0.75rem' }}>{key}</span> {val}</span>
                                            {q.correctAnswer === key && <span>✓</span>}
                                        </div>
                                    )
                                ))}
                            </div>

                            {q.explanation && (
                                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#fffbeb', borderRadius: '0.75rem', fontSize: '0.875rem', color: '#92400e', border: '1px solid #fef3c7', fontStyle: 'italic' }}>
                                    <strong>Solution Insight:</strong> {q.explanation}
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'end', gap: '0.75rem', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9' }}>
                                <button className="btn-action" onClick={() => openEditModal(q)}>Refine</button>
                                <button className="btn-action" style={{ color: '#dc2626' }} onClick={() => handleDelete(q._id)}>Delete</button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="admin-table-section-card" style={{ textAlign: 'center', padding: '5rem', borderStyle: 'dashed', background: 'transparent' }}>
                        <p style={{ color: '#94a3b8', fontSize: '1.125rem' }}>No questions have been designated for this assessment module.</p>
                        <button className="btn-action primary" style={{ marginTop: '1.5rem' }} onClick={openCreateModal}>Initialize First Item</button>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="admin-table-section-card" style={{ width: '100%', maxWidth: '800px', padding: '3rem' }}>
                        <h3 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '2.5rem' }}>{editingQuestion ? 'Refine Item Metadata' : 'Design New Item'}</h3>
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                            <div>
                                <label className="label-caps" style={{ marginBottom: '1rem', display: 'block' }}>Architectural Type</label>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <button type="button" className={`btn-action ${formData.type === 'mcq' ? 'primary' : ''}`} style={{ flex: 1 }} onClick={() => handleTypeChange('mcq')}>Multiple Choice</button>
                                    <button type="button" className={`btn-action ${formData.type === 'tf' ? 'primary' : ''}`} style={{ flex: 1 }} onClick={() => handleTypeChange('tf')}>True / False</button>
                                </div>
                            </div>

                            <div>
                                <label className="label-caps" style={{ marginBottom: '0.75rem', display: 'block' }}>Question Designation</label>
                                <textarea
                                    required
                                    style={{ width: '100%', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', minHeight: '100px' }}
                                    value={formData.questionText}
                                    onChange={e => setFormData({ ...formData, questionText: e.target.value })}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                                {formData.type === 'mcq' ? (
                                    ['A', 'B', 'C', 'D'].map(opt => (
                                        <div key={opt}>
                                            <label style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.5rem', display: 'block' }}>KEY {opt}</label>
                                            <input
                                                type="text"
                                                required
                                                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                                                value={formData.options[opt]}
                                                onChange={e => setFormData({ ...formData, options: { ...formData.options, [opt]: e.target.value } })}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    ['A', 'B'].map((opt, i) => (
                                        <div key={opt}>
                                            <label style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.5rem', display: 'block' }}>KEY {opt}</label>
                                            <input type="text" readOnly style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', background: '#f8fafc' }} value={i === 0 ? 'True' : 'False'} />
                                        </div>
                                    ))
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                                <div>
                                    <label className="label-caps" style={{ marginBottom: '0.75rem', display: 'block' }}>Validation Key</label>
                                    <select
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                                        value={formData.correctAnswer}
                                        onChange={e => setFormData({ ...formData, correctAnswer: e.target.value })}
                                    >
                                        <option value="A">{formData.type === 'tf' ? 'True' : 'Key A'}</option>
                                        <option value="B">{formData.type === 'tf' ? 'False' : 'Key B'}</option>
                                        {formData.type === 'mcq' && (
                                            <>
                                                <option value="C">Key C</option>
                                                <option value="D">Key D</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-caps" style={{ marginBottom: '0.75rem', display: 'block' }}>Point Allocation</label>
                                    <input
                                        type="number"
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                                        value={formData.points}
                                        onChange={e => setFormData({ ...formData, points: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'end', gap: '1rem', marginTop: '1rem' }}>
                                <button type="button" className="btn-action" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-action primary">Commit Item</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.singleButton ? "Ok" : "Confirm"}
                cancelText={confirmModal.singleButton ? null : "Cancel"}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />
        </>

    );
}
